import React, { useState, useEffect, useRef, useCallback } from "react";
import Select, { components } from "react-select";
import { capitalizeFirstLetter, classNames, getNameProfile } from "Utils/utils";
import "./Select.css";
import { isArray, isEmpty } from "lodash";
import Loader from "../Loader/Loader";

/** Red option row + badge in control when participant funding is expired */
const ExpiredFundingOption = (props) => {
  const expired = Boolean(props.data?.is_expired);
  return (
    <components.Option {...props}>
      <span
        className={
          expired
            ? "text-red-600 font-semibold normal-case"
            : "text-gray-900 normal-case"
        }
      >
        {props.data?.label}
      </span>
    </components.Option>
  );
};

const ExpiredFundingSingleValue = (props) => {
  const expired = Boolean(props.data?.is_expired);
  const contractId =
    props.data?.contract_id ||
    String(props.data?.label || "")
      .replace(/\s*\(Expired\)\s*$/i, "")
      .trim();
  if (!expired) {
    return (
      <components.SingleValue {...props}>
        <span className="normal-case">
          {capitalizeFirstLetter(props.data?.label)}
        </span>
      </components.SingleValue>
    );
  }
  return (
    <components.SingleValue {...props}>
      <span className="flex items-center gap-2 min-w-0 normal-case">
        <span
          className="shrink-0 rounded-md bg-red-600 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white shadow-sm"
          title="This funding period has ended"
        >
          Expired
        </span>
        <span className="truncate font-semibold text-red-700">{contractId}</span>
      </span>
    </components.SingleValue>
  );
};

const SelectOption = (props) => {
  const {
    data,
    selectedOption,
    handleChange,
    placeHolder,
    isSearch,
    name,
    cssClass,
    isDisabled,
    isMulti = false,
    defaultValue,
    isClearable = false,
    loading = false,
    showIcon = false,
    profileFieldMargin,
    register = null,
    isRequired = false,
    errorMessage = null,
    setValue = null,
    clearErrors,
    validate,
    noOptionsMessage = "No Options",
    setIsTimeChanged,
    setActualDate,
    isValueChanges,
    isClearCall,
    onFocusHandler,
    isApiLoading = false,
    isManagerModule = false,
    altName = null,
    newDesign=false,
    placeholderText,
    isForRostering=false,
    setPage,
    setSelectedWorkerInSidebar,
    isTimesheetSidebar,
    maxHeight,
    onMenuOpen,
    onMenuClose,
    useMenuPortal = false, // Only use portal when explicitly needed (e.g., in modals)
    menuPortalZIndex,
    /**
     * Opt-in react-select `closeMenuOnScroll` (boolean | (event) => boolean).
     * A portalled menu is positioned when it opens and never follows the control,
     * so screens with scrollable panels can close it instead of letting it drift.
     */
    closeMenuOnScroll = false,
    /** Opt-in: disable react-select auto scroll-into-view — pairs with closeMenuOnScroll. */
    menuShouldScrollIntoView = true,
    /** When true, keeps the selected option visible (needed for nested RHF field names). */
    persistSelectedOption = false,
    /** When true with persistSelectedOption, do not clear RHF value if selectedOption is briefly null. */
    preserveSelectionWhenOptionMissing = false,
    /**
     * When true, do not attach RHF's ref to react-select.
     * RHF sets mount=false on ref(null) during remounts, which skips required
     * validation — opt-in only (e.g. rostering Service Category).
     */
    detachRegisterRef = false,
    menuMinWidth, // Optional: e.g. '150px' so opened menu matches narrow control width
    /** Participant funding: red options + badge + tinted control when expired */
    highlightExpiredFunding = false,
    /** When set, forwarded to react-select; use meta.context === 'value' | 'menu' */
    formatOptionLabel: formatOptionLabelProp = undefined,
    centerPlaceholder = false,
  } = props;

  const [inComponentSelectedOpt, setInComponentSelectedOpt] = useState({});
  const lastSyncedFormValueRef = useRef(undefined);
  // Gate effect side-effects to real value changes only. Parent often passes a
  // new isValueChanges/clearErrors identity each render (inline arrows), which
  // previously re-fired clearErrors → formState update → max update depth.
  const lastEmittedInternalValueRef = useRef(undefined);
  // True between user onChange and parent selectedOption catching up. Prevents
  // the sync effect from wiping single-select (worker/participant) mid-pick.
  const pendingUserSelectionRef = useRef(false);
  const isValueChangesRef = useRef(isValueChanges);
  const clearErrorsRef = useRef(clearErrors);
  isValueChangesRef.current = isValueChanges;
  clearErrorsRef.current = clearErrors;

  const isInvoiceNdisRateField =
    typeof name === "string" && name.includes("invoice_ndis_rate");

  const syncFormValue = useCallback(
    (nextVal) => {
      if (!setValue || !name) return;
      // Never write a full option object into RHF — that breaks selectedOption lookups
      const primitive =
        nextVal != null &&
        typeof nextVal === "object" &&
        !Array.isArray(nextVal) &&
        Object.prototype.hasOwnProperty.call(nextVal, "value")
          ? nextVal.value
          : nextVal;
      const normalized = primitive ?? null;
      if (String(lastSyncedFormValueRef.current) === String(normalized)) {
        return;
      }
      lastSyncedFormValueRef.current = normalized;
      setValue(name, normalized);
    },
    [setValue, name]
  );

  /** Apply a single-select user gesture immediately (state + RHF), before effects. */
  const applyUserSingleSelect = useCallback(
    (e) => {
      if (e == null) {
        pendingUserSelectionRef.current = false;
        lastEmittedInternalValueRef.current = undefined;
        setInComponentSelectedOpt(null);
        if (setValue && name) {
          lastSyncedFormValueRef.current = null;
          setValue(name, null);
        }
        return;
      }

      pendingUserSelectionRef.current = true;
      setInComponentSelectedOpt(e);

      const next = e?.value ?? null;
      const nextKey = next == null ? "" : String(next);

      if (setValue && name) {
        if (String(lastSyncedFormValueRef.current) !== String(next)) {
          lastSyncedFormValueRef.current = next;
          setValue(name, next);
        }
        const onClearErrors = clearErrorsRef.current;
        if (
          typeof onClearErrors === "function" &&
          next != null &&
          String(next).trim() !== ""
        ) {
          onClearErrors(name);
        }
      }

      // Emit change side-effects once from the gesture; effect 2 will no-op.
      if (lastEmittedInternalValueRef.current !== nextKey) {
        lastEmittedInternalValueRef.current = nextKey;
        const onValueChanges = isValueChangesRef.current;
        if (typeof onValueChanges === "function") {
          onValueChanges(true, e.uuid ?? e.value);
        }
      }
    },
    [setValue, name]
  );

  // Rostering shift modal swallows non-portaled menu clicks via form onMouseDown
  // on `.roleSelector` (group MultiSelect already portals — that is why multi works).
  const shouldPortalMenu = useMenuPortal || isForRostering;
  const portalZIndex = shouldPortalMenu
    ? menuPortalZIndex ?? (isForRostering ? 10050 : 10010)
    : 10010;
  /** Portaled menus must use fixed positioning (sticky/transform ancestors break absolute). */
  const portaledMenuProps = shouldPortalMenu
    ? { menuPortalTarget: document.body, menuPosition: "fixed" }
    : {};
  const multiPortaledMenuProps = useMenuPortal
    ? { menuPortalTarget: document.body, menuPosition: "fixed" }
    : {};

  const sameOptionValue = (a, b) => {
    if (a == null && b == null) return true;
    if (a == null || b == null) return false;
    if (isArray(a) || isArray(b)) {
      if (!isArray(a) || !isArray(b) || a.length !== b.length) return false;
      return a.every(
        (item, idx) =>
          String(item?.value ?? item) === String(b[idx]?.value ?? b[idx])
      );
    }
    return String(a?.value ?? a) === String(b?.value ?? b);
  };

  const preferExternalSelectedOption =
    name === "invoice_service_type_id" ||
    (typeof name === "string" && name.includes("invoice_service_type_id")) ||
    altName === "google_state_id" ||
    (typeof name === "string" && name.includes("invoice_item_id")) ||
    isInvoiceNdisRateField;

  // react-select's default getOptionValue does `option.value` and throws if option is undefined.
  const getOptionValueSafe = (option) => option?.value;
  const getOptionLabelSafe = (option) =>
    capitalizeFirstLetter(option?.label);
  const selectOptions = (Array.isArray(data) ? data : []).filter(
    (opt) => opt != null
  );

  const sanitizeSelectValue = (val) => {
    if (val == null) return null;
    if (isArray(val)) {
      return val.filter((opt) => opt != null);
    }
    // Initial internal state is `{}` — never pass that to react-select.
    if (
      typeof val === "object" &&
      isEmpty(val) &&
      (val.value == null || val.value === "")
    ) {
      return null;
    }
    return val;
  };

  const getSelectValue = () => {
    // Controlled fields (e.g. Service Category after participant change) must
    // follow the parent `selectedOption` — preferring stale internal state
    // leaves the previous label visible and overlaps the new one (V360-5366).
    let resolved = null;
    if (preferExternalSelectedOption) {
      if (selectedOption != null) {
        resolved = selectedOption;
      } else if (
        !isArray(inComponentSelectedOpt) &&
        inComponentSelectedOpt &&
        inComponentSelectedOpt.value != null &&
        inComponentSelectedOpt.value !== ""
      ) {
        resolved = inComponentSelectedOpt;
      }
    } else if (isMulti) {
      // Prefer parent for multi; internal array is only a fallback.
      if (selectedOption != null) {
        resolved = selectedOption;
      } else if (isArray(inComponentSelectedOpt)) {
        resolved = inComponentSelectedOpt;
      }
    } else if (
      !isArray(inComponentSelectedOpt) &&
      inComponentSelectedOpt &&
      inComponentSelectedOpt.value != null &&
      inComponentSelectedOpt.value !== ""
    ) {
      resolved = inComponentSelectedOpt;
    } else if (selectedOption != null) {
      resolved = selectedOption;
    }
    return sanitizeSelectValue(resolved);
  };

  const customStyle = {
    menuPortal: (base) => ({ 
      ...base, 
      zIndex: portalZIndex,
    }),
    menu: (base) => ({ 
      ...base, 
      zIndex: portalZIndex,
      // ...(menuMinWidth ? { minWidth: menuMinWidth, width: menuMinWidth } : { minWidth: '200px' }),
      // marginTop: '4px',
    }),
    input: (provided) => ({
      ...provided,
      width: "100%",
      minWidth: "2px",
      fontSize: "12px",
    }),
    control: (base) => ({
      ...base,
      fontSize: "12px",
    }),
    singleValue: (base) => ({
      ...base,
      fontSize: "12px",
    }),
    placeholder: (base) => ({
      ...base,
      fontSize: "12px",
      ...(centerPlaceholder ? { textAlign: "center", width: "100%" } : {}),
    }),
    groupHeading: (base) => ({
      ...base,
      color: "#1f3b73",
      backgroundColor: "#eef4ff",
      borderTop: "1px solid #d8e3f8",
      borderBottom: "1px solid #d8e3f8",
      fontSize: "11px",
      fontWeight: 700,
      letterSpacing: "0.04em",
      margin: 0,
      padding: "7px 10px",
      textTransform: "uppercase",
    }),
     ...(newDesign
      ? {
            menuList: (base) => ({
              ...base,
              maxHeight: isForRostering ? "150px" : maxHeight ? maxHeight : "200px",
              overflowY: "auto",
            }),
            groupHeading: (base) => ({
              ...base,
              color: "#1f3b73",
              backgroundColor: "#eef4ff",
              borderTop: "1px solid #d8e3f8",
              borderBottom: "1px solid #d8e3f8",
              fontSize: "11px",
              fontWeight: 700,
              letterSpacing: "0.04em",
              margin: 0,
              padding: "7px 10px",
              textTransform: "uppercase",
            }),
            option: (base) => ({
              ...base,
              fontSize: "12px",
            }),
        }
      : {
            menuList: (base) => ({
              ...base,
              maxHeight: isForRostering ? "150px" : maxHeight ? maxHeight : "200px",
              overflowY: "auto",
            }),
            option: (base) => ({
              ...base,
              fontSize: "12px",
            }),
      }),
     
    ...(newDesign
      ? {
          dropdownIndicator: (provided) => ({
            ...provided,
            color: "#617DBF",
            padding: "8px 0px",
          }),
          indicatorSeparator: () => ({
            display: "none",
          }),
        }
      : {}),
  };

  const fundingExpiredSingleSelectStyles = highlightExpiredFunding
    ? {
        ...customStyle,
        ...(selectedOption?.is_expired
          ? {
              control: (base) => {
                const inner = customStyle.control(base);
                return {
                  ...inner,
                  borderColor: "#f87171",
                  boxShadow: "0 0 0 1px #f87171",
                  backgroundColor: "#fff7f7",
                };
              },
            }
          : {}),
        option: (base, state) => {
          const inner = customStyle.option
            ? customStyle.option(base)
            : base;
          if (state.data?.is_expired) {
            return {
              ...inner,
              backgroundColor: state.isFocused ? "#fecaca" : "#fff1f2",
            };
          }
          return inner;
        },
      }
    : customStyle;

  useEffect(() => {
    if (
      selectedOption !== null &&
      selectedOption !== undefined &&
      selectedOption &&
      setValue &&
      register
    ) {
      // Single-select must not ingest multi-worker arrays (group shift) — causes setValue(undefined) loops
      if (!isMulti && isArray(selectedOption)) {
        return;
      }

      if (isMulti && isArray(selectedOption) && selectedOption.length > 0) {
        if (!sameOptionValue(inComponentSelectedOpt, selectedOption)) {
          setInComponentSelectedOpt(selectedOption);
        }
        return;
      }

      const keepSelectedOptionInSync = preferExternalSelectedOption;

      if (
        isMulti === false &&
        inComponentSelectedOpt &&
        String(inComponentSelectedOpt.value) === String(selectedOption?.value)
      ) {
        /* Same value — skip state updates to avoid setValue ↔ re-render loops */
        pendingUserSelectionRef.current = false;
        if (keepSelectedOptionInSync) {
          syncFormValue(selectedOption?.value);
        }
        return;
      }

      if (isMulti === false && isEmpty(inComponentSelectedOpt)) {
        if (!sameOptionValue(inComponentSelectedOpt, selectedOption)) {
          setInComponentSelectedOpt(selectedOption);
        }
        if (keepSelectedOptionInSync) {
          syncFormValue(selectedOption?.value);
        }
        return;
      }

      // User just picked a value; parent selectedOption is still stale for a
      // render. Do not overwrite/clear internal state (breaks single-select).
      if (pendingUserSelectionRef.current && isMulti === false) {
        return;
      }

      if (keepSelectedOptionInSync) {
        syncFormValue(selectedOption?.value);
        if (!sameOptionValue(inComponentSelectedOpt, selectedOption)) {
          setInComponentSelectedOpt(selectedOption);
        }
      } else if (persistSelectedOption) {
        const nextVal = selectedOption?.value ? selectedOption?.value : null;
        syncFormValue(nextVal);
        if (!sameOptionValue(inComponentSelectedOpt, selectedOption)) {
          setInComponentSelectedOpt(selectedOption);
        }
      } else {
        syncFormValue(selectedOption?.value);
        if (
          inComponentSelectedOpt !== null &&
          !isEmpty(inComponentSelectedOpt)
        ) {
          setInComponentSelectedOpt(null);
        }
      }
    } else if (
      (selectedOption == null || selectedOption === undefined) &&
      inComponentSelectedOpt !== null &&
      !isEmpty(inComponentSelectedOpt) &&
      setValue &&
      register
    ) {
      if (isInvoiceNdisRateField) {
        return;
      }
      // Single-select pick in flight (or option list lagging) — never wipe.
      if (pendingUserSelectionRef.current) {
        return;
      }
      if (preserveSelectionWhenOptionMissing && persistSelectedOption) {
        // Keep current selection when option is temporarily missing from list
      } else if (
        name === "invoice_service_type_id" ||
        name === "worker_id" ||
        (typeof name === "string" &&
          name.endsWith(".invoice_service_type_id"))
      ) {
        // Clear display state only. Wiping the form value when options briefly
        // fail to resolve (id type mismatch / options still loading) races with
        // participant-driven category updates and leaves overlapping labels.
        // Also covers nested split fields: splitShifts.{n}.invoice_service_type_id
        // worker_id: Add Shift participant change refreshes worker options and
        // briefly misses the selected worker — do not wipe the form value.
        setInComponentSelectedOpt(null);
      } else {
        lastSyncedFormValueRef.current = undefined;
        setValue(name, null);
        setInComponentSelectedOpt(null);
      }
    }

    if (register === null && setValue === null) {
      const next = selectedOption ?? null;
      if (!sameOptionValue(inComponentSelectedOpt, next)) {
        setInComponentSelectedOpt(next);
      }
    }
    // Depend on value/label, not object identity — parent often passes a new option
    // object each watch() render (Split breakdown), which caused max-depth loops.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- selectedOption by value
  }, [
    selectedOption?.value,
    selectedOption?.label,
    selectedOption?.number,
    selectedOption?.price,
    syncFormValue,
    name,
    register,
    setValue,
    isInvoiceNdisRateField,
    isMulti,
    altName,
    preferExternalSelectedOption,
    persistSelectedOption,
    preserveSelectionWhenOptionMissing,
  ]);

  useEffect(() => {
    const hasInternalValue =
      inComponentSelectedOpt &&
      !isArray(inComponentSelectedOpt) &&
      Object.prototype.hasOwnProperty.call(inComponentSelectedOpt, "value");

    // Reset emit gate on clear so the same value can be re-selected later.
    if (!hasInternalValue || isEmpty(inComponentSelectedOpt)) {
      lastEmittedInternalValueRef.current = undefined;
      return;
    }

    if (!setValue || !register) {
      return;
    }

    // Parent cleared selection (e.g. split service-category change). Do not
    // push stale inComponentSelectedOpt back into the form — that re-applied
    // Daily Life support items onto other categories.
    if (
      typeof name === "string" &&
      name.includes("invoice_item_id") &&
      (selectedOption == null || selectedOption === undefined)
    ) {
      return;
    }

    const nextValue = inComponentSelectedOpt.value;
    const nextKey = nextValue == null ? "" : String(nextValue);

    // Emit once per distinct internal value. Avoids clearErrors/isValueChanges
    // re-entry when parent re-renders with a new callback identity.
    if (lastEmittedInternalValueRef.current === nextKey) {
      return;
    }
    lastEmittedInternalValueRef.current = nextKey;

    const onValueChanges = isValueChangesRef.current;
    if (
      typeof onValueChanges === "function" &&
      String(nextValue) !== String(selectedOption?.value) &&
      !isArray(selectedOption)
    ) {
      onValueChanges(true, inComponentSelectedOpt.uuid);
    }

    syncFormValue(nextValue);

    // Clear field errors only after a real value is synced — never when
    // errorMessage becomes truthy (that wiped required errors on submit).
    const onClearErrors = clearErrorsRef.current;
    if (
      typeof onClearErrors === "function" &&
      name &&
      nextValue != null &&
      String(nextValue).trim() !== ""
    ) {
      onClearErrors(name);
    }
  }, [
    inComponentSelectedOpt,
    syncFormValue,
    selectedOption?.value,
    name,
    register,
    setValue,
  ]);

  const getIcon = (type) => {
   
    return type;
  };

  const handleImageError = (e, fname, lname) => {
    // color=fff
    return (e.target.src = getNameProfile(fname, lname, "0D8ABC"));
  };

  const addRegisterComponent = () => {
    if (register && register !== null) {
      const registeredValue =
        selectedOption &&
        inComponentSelectedOpt &&
        !isArray(inComponentSelectedOpt) &&
        inComponentSelectedOpt.value != null
          ? inComponentSelectedOpt.value
          : null;
      const registered = register(
        name,
        detachRegisterRef
          ? {
              // Opt-in path: real value validation (no radio `value` option, no ref).
              required: isRequired ? "This field is required!" : false,
              validate: (fieldValue) => {
                if (typeof validate === "function") {
                  return validate(fieldValue);
                }
                if (
                  isRequired &&
                  (fieldValue == null || String(fieldValue).trim() === "")
                ) {
                  return "This field is required!";
                }
                return true;
              },
            }
          : {
              value: registeredValue,
              // disabled: isDisabled,
              required: isRequired,
              validate: () => {
                return validate;
              },
            }
      );
      // Opt-in: keep field mounted for RHF required checks (react-select remounts).
      if (detachRegisterRef) {
        const { ref: _unusedRegisterRef, ...rest } = registered;
        return rest;
      }
      return { ...registered };
    } else {
      return;
    }
  };

  // const ValueContainer = (containerProps) => {
  //   const { children } = containerProps;
  //   if (children[0]) {
  //     const { length } = children[0];
  //     const tmpChildren = [...children];
  //     if (length >= 2) {
  //       tmpChildren[0] = `${length} selected`;
  //     }
  //     return (
  //       <components.ValueContainer {...containerProps}>
  //         {tmpChildren}
  //       </components.ValueContainer>
  //     );
  //   }
  //   return (
  //     <components.ValueContainer {...containerProps}>
  //       {children}
  //     </components.ValueContainer>
  //   );
  // };

  // Put title on the label — do not wrap MultiValue children in an extra div
  // (that breaks chip flex layout and causes grey background misalignment).
  const CustomMultiValueLabel = (labelProps) => (
    <components.MultiValueLabel
      {...labelProps}
      innerProps={{
        ...labelProps.innerProps,
        title: labelProps.data?.label,
      }}
    />
  );

  const CustomValueContainer = ({ children, ...containerProps }) => {
    return (
      <components.ValueContainer {...containerProps}>
        <div
          className='selected-options-container'
          style={{
            display: "flex",
            overflowX: "auto",
            whiteSpace: "nowrap",
            // maxWidth: "230px",
          }}
        >
          {children}
        </div>
      </components.ValueContainer>
    );
  };

  const isCompactMultiSelect =
    typeof cssClass === "string" &&
    cssClass.includes("roster-compact-multiselect");
  if (showIcon) {
    const CustomSelectValue = (csprops) => {
      return (
        <components.SingleValue {...csprops}>
          <div className='flex items-center capitalize'>
            {csprops.data.label && csprops.data.icon && (
              <img
                onError={(e) => handleImageError(e, csprops.data.label, " ")}
                className='w-7 h-7 bg-gray-300 rounded-full flex-shrink-0 object-cover mr-2'
                src={getIcon(csprops.data.icon || csprops.data.value)}
                alt=''
              />
            )}
            {csprops.data.label}
          </div>
        </components.SingleValue>
      );
    };

    const CustomOption = (props) => {
      const { data } = props;
      return (
        <components.Option {...props}>
          <div className='flex items-center capitalize'>
            {data.label && data.icon && (
              <img
                className='w-7 h-7 bg-gray-300 rounded-full flex-shrink-0 object-cover mr-2'
                src={getIcon(data.icon || data.value)}
                alt=''
              />
            )}
            {data.label}
          </div>
        </components.Option>
      );
    };

    return (
      <>
        <Select
          {...addRegisterComponent()}
          value={getSelectValue()}
          getOptionValue={getOptionValueSafe}
          getOptionLabel={getOptionLabelSafe}
          onChange={(e) => {
            if (e === null && isClearCall) {
              pendingUserSelectionRef.current = false;
              lastEmittedInternalValueRef.current = undefined;
              lastSyncedFormValueRef.current = null;
              setInComponentSelectedOpt(null);
              isClearCall();
              return;
            }

            if (isTimesheetSidebar && handleChange) {
              setSelectedWorkerInSidebar(null);
            }
            applyUserSingleSelect(e);
            if (handleChange) {
              handleChange(e);
            }
          }}
          options={selectOptions}
          components={{
            Option: CustomOption,
            SingleValue: CustomSelectValue,
          }}
          isSearchable={isSearch}
          isLoading={loading}
          isClearable={isClearable}
          isDisabled={isDisabled}
          placeholder={placeHolder}
          menuShouldScrollIntoView={menuShouldScrollIntoView}
          closeMenuOnScroll={closeMenuOnScroll}
          {...portaledMenuProps}
          className={classNames(
            `w-100 roleSelector scrollOptions capitalize`,
            cssClass,
            errorMessage?.length > 0 && "errorBorderForSelect"
          )}
          noOptionsMessage={() => noOptionsMessage}
          styles={{
            menuPortal: (base) => ({ ...base, zIndex: portalZIndex }),
            menu: (base) => ({ 
              ...base, 
              zIndex: portalZIndex,
              marginTop: '4px',
            }),
            menuList: (base) => ({
              ...base,
              maxHeight: isForRostering ? "150px" : maxHeight ? maxHeight : "200px",
              overflowY: "auto",
            }),
            ...(newDesign ? {
              option: (base) => ({
                ...base,
                fontSize: "12px",
              }),
            } : {}),
          }}
        />
        {errorMessage && (
          <span style={{ fontSize: "13px", color: "red", marginLeft: "5px" }}>
            {errorMessage}
          </span>
        )}
      </>
    );
  }

  return (
    <>
      {isMulti ? (
        <Select
          {...addRegisterComponent()}
          value={getSelectValue()}
          getOptionValue={getOptionValueSafe}
          getOptionLabel={getOptionLabelSafe}
          // menuPlacement='auto'
          closeMenuOnSelect={false}
          components={
            isManagerModule
              ? {
                  MultiValueLabel: CustomMultiValueLabel,
                  ValueContainer: CustomValueContainer,
                }
              : { MultiValueLabel: CustomMultiValueLabel }
          }
          // components={animatedComponents}
          isMulti
          options={selectOptions}
          onChange={(e) => {
            if (handleChange) handleChange(e);
            else {
              setInComponentSelectedOpt(e);
            }
          }}
          {...(useMenuPortal ? multiPortaledMenuProps : {})}
          menuPlacement='auto'
          closeMenuOnScroll={closeMenuOnScroll}
          {...(menuShouldScrollIntoView === false
            ? { menuShouldScrollIntoView: false }
            : {})}
          isDisabled={isDisabled}
          name={name}
          isClearable={isClearable}
          isLoading={loading}
          isSearchable={isSearch}
          placeholder={placeHolder}
          // isClearable={isClearable}
          className={classNames(
            `w-100 roleSelector scrollOptions capitalize ${cssClass}`,
            errorMessage?.length > 0 && "errorBorderForSelect"
          )}
          // hideSelectedOptions
          // components={{ ValueContainer }}
          noOptionsMessage={() =>
            isApiLoading ? (
              <div className='flex justify-center'>
                {" "}
                <Loader
                  isSmallView={true}
                  cssClass={"text-black font-bo  mr-0 p-0 flex"}
                />
              </div>
            ) : (
              noOptionsMessage
            )
          }
          onFocus={onFocusHandler}
          styles={
            isCompactMultiSelect
              ? {
                  control: (base) => ({
                    ...base,
                    minHeight: 36,
                    height: 36,
                    alignItems: "center",
                    fontSize: "12px",
                  }),
                  valueContainer: (base) => ({
                    ...base,
                    display: "flex",
                    flexWrap: "nowrap",
                    alignItems: "center",
                    height: 34,
                    maxHeight: 34,
                    paddingTop: 0,
                    paddingBottom: 0,
                    overflowX: "auto",
                    overflowY: "hidden",
                    fontSize: "12px",
                  }),
                  multiValue: (base) => ({
                    ...base,
                    margin: "0 3px 0 0",
                    alignItems: "center",
                    minHeight: 22,
                    height: 22,
                    borderRadius: 4,
                    backgroundColor: "#eef2ff",
                    flexShrink: 0,
                  }),
                  multiValueLabel: (base) => ({
                    ...base,
                    fontSize: "11px",
                    lineHeight: "16px",
                    padding: "2px 6px",
                    color: "#1e293b",
                    whiteSpace: "nowrap",
                  }),
                  multiValueRemove: (base) => ({
                    ...base,
                    paddingLeft: 2,
                    paddingRight: 4,
                    color: "#64748b",
                    ":hover": {
                      backgroundColor: "#e0e7ff",
                      color: "#0f172a",
                    },
                  }),
                  indicatorsContainer: (base) => ({
                    ...base,
                    height: 34,
                    alignItems: "center",
                  }),
                  placeholder: (base) => ({
                    ...base,
                    fontSize: "12px",
                  }),
                  input: (base) => ({
                    ...base,
                    fontSize: "12px",
                    margin: 0,
                    padding: 0,
                  }),
                  option: (base) => ({
                    ...base,
                    fontSize: "12px",
                  }),
                  menuPortal: (base) => ({ ...base, zIndex: 99999 }),
                }
              : {
                  menuPortal: customStyle.menuPortal,
                  menu: customStyle.menu,
                  ...(newDesign && customStyle.menuList
                    ? { menuList: customStyle.menuList, option: customStyle.option }
                    : {}),
                  ...(newDesign && customStyle.dropdownIndicator
                    ? {
                        dropdownIndicator: customStyle.dropdownIndicator,
                        indicatorSeparator: customStyle.indicatorSeparator,
                      }
                    : {}),
                  valueContainer: (base, state) => ({
                    ...base,
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: state.hasValue ? "flex-start" : "center",
                    maxHeight: "100%",
                    overflowY: "auto",
                    fontSize: "12px",
                  }),
                  multiValue: (base) => ({
                    ...base,
                    margin: "2px",
                    fontSize: "12px",
                  }),
                  multiValueLabel: (base) => ({
                    ...base,
                    fontSize: "12px",
                  }),
                  control: (base, state) => ({
                    ...base,
                    minHeight: "40px",
                    alignItems: state.hasValue ? "flex-start" : "center",
                    fontSize: "12px",
                  }),
                  placeholder: (base) => ({
                    ...base,
                    fontSize: "12px",
                    margin: 0,
                  }),
                  input: (base, state) => ({
                    ...base,
                    fontSize: "12px",
                    margin: 0,
                    padding: 0,
                    ...(!state.hasValue ? { width: 0, minWidth: 0 } : {}),
                  }),
                  option: (base) => ({
                    ...base,
                    fontSize: "12px",
                  }),
                }
          }
        />
      ) : (
        <Select
          {...addRegisterComponent()}
          isLoading={loading}
          isClearable={isClearable}
          isDisabled={isDisabled}
          name={name}
          getOptionValue={getOptionValueSafe}
          getOptionLabel={
            highlightExpiredFunding
              ? undefined
              : getOptionLabelSafe
          }
          {...(typeof formatOptionLabelProp === "function"
            ? { formatOptionLabel: formatOptionLabelProp }
            : {})}
          components={
            highlightExpiredFunding
              ? {
                  Option: ExpiredFundingOption,
                  SingleValue: ExpiredFundingSingleValue,
                }
              : typeof formatOptionLabelProp === "function"
              ? {
                  SingleValue: (svProps) => (
                    <components.SingleValue {...svProps}>
                      {formatOptionLabelProp(svProps.data, {
                        context: "value",
                        inputValue: svProps.selectProps?.inputValue ?? "",
                        selectValue: svProps.selectProps?.value,
                      })}
                    </components.SingleValue>
                  ),
                }
              : undefined
          }
          isSearchable={isSearch}
          options={selectOptions}
          blurInputOnSelect={true}
          menuPlacement='auto'
          menuShouldScrollIntoView={menuShouldScrollIntoView}
          closeMenuOnScroll={closeMenuOnScroll}
          placeholder={placeholderText ?  placeholderText:placeHolder}
          value={getSelectValue()}
          onChange={(e) => {
            if (e === null && isClearCall) {
              pendingUserSelectionRef.current = false;
              lastEmittedInternalValueRef.current = undefined;
              lastSyncedFormValueRef.current = null;
              setInComponentSelectedOpt(null);
              isClearCall();
              return;
            }
            if (setIsTimeChanged) {
              setIsTimeChanged(true);
            }
            if (setActualDate) {
              setActualDate(true);
            }
            if (setPage) {
              setPage(1);
            }
            applyUserSingleSelect(e);
            if (handleChange) {
              handleChange(e);
            }
          }}
          styles={
            highlightExpiredFunding
              ? fundingExpiredSingleSelectStyles
              : customStyle
          }
          {...portaledMenuProps}
          className={classNames(
            `select-basic-single w-100 roleSelector scrollOptions`,
            highlightExpiredFunding ? "normal-case" : "",
            cssClass,
            errorMessage ? "errorBorderForSelect" : ""
          )}
          noOptionsMessage={() => noOptionsMessage}
          onMenuOpen={onMenuOpen}
          onMenuClose={onMenuClose}
        />
      )}

      {errorMessage ? (
        <span
          style={{
            fontSize: "13px",
            color: "red",
            // marginLeft: "5px"
          }}
          // className='capitalize'
        >
          {errorMessage}
        </span>
      ) : (
        ""
      )}
    </>
  );
};

export default SelectOption;
